import { memo } from 'react'
import { isObject, isString } from '../../../utils'
import useId from '../../hooks/useId'
import Icon from '../Icon'
import {
  CardHeader,
  CardBody,
  CardFooter,
  CardWrapper,
  InCartIcon,
  CardContentWrapper,
  CardContent,
  WarningWrapper,
  WarningMessage
} from './ItemCard.styles'
import { ItemCardContentProps, ItemCardProps } from './ItemCard.types'
import SideContent from './SideContent'
import { mergeItemCardProps, validateMemo } from './ItemCard.utils'
import useMappedProps from '../../hooks/useMappedProps'
import ItemCardOverlay from './components/ItemCardOverlay'
import { OVERLAY_TYPE_READ_ONLY } from './ItemCard.constants'

const ItemCard = memo(
  ({
    item = {
      quantity: 0
    },
    actions,
    className = '',
    layout = 'none',
    standalone = false,
    'data-testid': testid = 'item',
    isOnCart = false,
    ...props
  }: ItemCardProps) => {
    const { getTestId } = useId(testid)
    const defaultProps = mergeItemCardProps(props, layout)
    const {
      gap = 8,
      padding = 16,
      horizontal = false,
      header,
      body,
      footer,
      fixedContentBody,
      fixedContentHeader,
      styles,
      description,
      limitText,
      stockText,
      stock,
      descriptionStock,
      image,
      price,
      counter,
      counterBundle,
      tags,
      promotionDetail,
      promotionLabel,
      promotionTag,
      inCartChecked,
      sideContent,
      fieldsToDisplay,
      packagePriceMap,
      maxQtyMessage,
      bundlesHandler,
      bundleTexts,
      showFixedBottomContent,
      allowLazyLoadingOnImages,
      packageTexts,
      outOfStockAction,
      ...otherProps
    } = defaultProps
    const inCartCheckedProps = isObject(inCartChecked) ? inCartChecked : {}
    const { icon } = inCartCheckedProps
    const inCartIcon = isString(icon) ? { name: icon } : icon
    const isChecked = inCartChecked && (item?.quantity || 0) > 0
    const isDisabled = Boolean(item?.outOfStock)
    const isReadOnly = item?.readOnly || item?.isHidden
    const overlayType = isReadOnly ? OVERLAY_TYPE_READ_ONLY : ''
    const mappedTags = useMappedProps(item, tags)
    const sideContentItems = useMappedProps(item, sideContent)
    const sideItemList = {
      top: sideContentItems?.filter(({ area = 'top' }) => area === 'top'),
      bottom: sideContentItems?.filter(({ area }) => area === 'bottom'),
      left: sideContentItems?.filter(({ area }) => area === 'left'),
      right: sideContentItems?.filter(({ area }) => area === 'right')
    }
    const iconProps = { name: 'MdError', color: '#f3b247', size: 14 }
    const warnText = maxQtyMessage

    const childrenProps: ItemCardContentProps = {
      name: defaultProps.name,
      description,
      limitText,
      stockText,
      stock,
      descriptionStock,
      image,
      price,
      counter,
      counterBundle,
      tags,
      mappedTags,
      promotionDetail,
      promotionLabel,
      promotionTag,
      fieldsToDisplay,
      packagePriceMap,
      bundleTexts,
      bundlesHandler,
      packageTexts,
      isOnCart,
      allowLazyLoadingOnImages
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleClick = (event: any, currentItem: any = item) => {
      event.preventDefault()
      event.stopPropagation()

      const isNotClickable =
        !!currentItem?.outOfStock ||
        !!currentItem?.readOnly ||
        !!currentItem?.isHidden

      if (actions && !isNotClickable) {
        actions({
          data: currentItem
        })
      }
    }

    return (
      <CardWrapper
        className={`ItemCard ${className}`}
        inCartStyles={inCartCheckedProps}
        isChecked={isChecked}
        hasActions={!!actions}
        sx={styles}
        data-testid={getTestId()}
        {...otherProps}>
        {isChecked && inCartIcon && (
          <InCartIcon color={inCartCheckedProps?.color}>
            <Icon name="MdCheck" {...inCartIcon} />
          </InCartIcon>
        )}
        {(isDisabled || isReadOnly) && (
          <ItemCardOverlay
            overlayType={overlayType}
            outOfStockAction={outOfStockAction}
            item={item}
          />
        )}
        <SideContent
          className="SideContentTop"
          area="top"
          items={sideItemList.top}
          data={item}
          standalone={standalone}
          data-testid={getTestId('side')}
        />
        <CardContentWrapper
          className="ItemCardWrapper"
          onClick={(e) => handleClick(e)}>
          <SideContent
            className="SideContentLeft"
            area="left"
            data={item}
            items={sideItemList.left}
            data-testid={getTestId('side')}
          />
          <CardContent
            className="ItemCardContent"
            gap={gap}
            padding={padding}
            horizontal={horizontal}>
            <CardHeader
              className="Header"
              item={item}
              gap={gap}
              data-testid={getTestId()}
              {...childrenProps}
              {...header}
            />
            <CardContent gap={gap} padding={0}>
              <CardBody
                className="Body"
                item={item}
                gap={gap}
                horizontal={horizontal}
                data-testid={getTestId()}
                {...childrenProps}
                {...body}
              />
              <CardFooter
                className="Footer"
                item={item}
                gap={gap}
                data-testid={getTestId()}
                {...childrenProps}
                {...footer}
              />
            </CardContent>
          </CardContent>
          <SideContent
            className="SideContentRight"
            area="right"
            items={sideItemList.right}
            data={item}
            data-testid={getTestId('side')}
          />
        </CardContentWrapper>
        <SideContent
          className="SideContentBottom"
          area="bottom"
          items={sideItemList.bottom}
          data={item}
          data-testid={getTestId('side')}
        />
        {!!item.itemsExtra?.length &&
          showFixedBottomContent &&
          item.itemsExtra.map((itemExtra) => (
            <CardContentWrapper
              key={itemExtra.sku}
              onClick={(e) => handleClick(e, itemExtra)}>
              <CardContent
                horizontal
                className="FixedBottomContent"
                gap={gap}
                borderRadius={1}
                margin={1}
                marginTop={0}
                isSubitem>
                <CardHeader
                  className="FixedBottomHeader"
                  item={itemExtra}
                  gap={gap}
                  data-testid={getTestId()}
                  {...childrenProps}
                  {...fixedContentHeader}
                />
                <CardBody
                  className="FixedBottomBody"
                  item={itemExtra}
                  gap={gap}
                  horizontal={horizontal}
                  data-testid={getTestId()}
                  {...childrenProps}
                  {...fixedContentBody}
                />
              </CardContent>
            </CardContentWrapper>
          ))}
        {item.maxQtyAlert && item.quantity > item.maxQtyAlert && (
          <WarningWrapper className="itemCard-warning-wrapper">
            <Icon {...iconProps} />
            <WarningMessage value={warnText} />
          </WarningWrapper>
        )}
      </CardWrapper>
    )
  },
  validateMemo
)

export default ItemCard
